import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { ArrowLeft, Loader2, Printer } from "lucide-react";

import { GET_SITUATION_SESSION_FOR_GROWNUP } from "@/graphql/queries/situations";
import {
  PrintedSheet,
  type PrintedSituation,
} from "@/components/situations/printed-sheet";
import { PrintOptions } from "@/pages/lab/PrintSituationPage";
import { Button } from "@/components/ui/button";

/**
 * A whole session on paper.
 *
 * **Each situation starts a new page**, always — they are separate stories, and a
 * second one beginning half way down a sheet reads as a continuation of the first.
 * „Po vieną klausimą lape" then breaks each *question* onto its own page as well,
 * which is what turns a three-situation session into a small booklet.
 *
 * One name line for the document rather than one per situation: it comes back as a
 * stapled set, not as loose sheets from different days.
 */
export default function PrintSituationSessionPage() {
  const { id = "" } = useParams();

  const [showNotes, setShowNotes] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [onePerPage, setOnePerPage] = useState(false);

  const { data, loading } = useQuery(GET_SITUATION_SESSION_FOR_GROWNUP, {
    variables: { id },
    skip: !id,
  });

  const session = (data as any)?.situationSession;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!session) {
    return <div className="py-12 text-center text-muted-foreground">Sesija nerasta</div>;
  }

  const situations: PrintedSituation[] = session.situations ?? [];
  const hasAnswers = situations.some((s) =>
    (s.questions ?? []).some((q) => q.answer || q.reasonAnswer || q.counterAnswer)
  );
  const hasNotes = situations.some((s) => Boolean(s.forGrownup));

  return (
    <>
      <div className="no-print mb-6 flex flex-wrap items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/lab/session/${id}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Spausdinimo peržiūra</h1>
          <p className="text-muted-foreground">
            Sesija · {situations.length} situacijos
            {session.personalized && (
              <span className="ml-2 text-primary">
                · pritaikyta
                {session.personalizationTheme ? ` (${session.personalizationTheme})` : ""}
              </span>
            )}
          </p>
        </div>

        <PrintOptions
          showNotes={showNotes}
          setShowNotes={setShowNotes}
          showAnswers={showAnswers}
          setShowAnswers={setShowAnswers}
          onePerPage={onePerPage}
          setOnePerPage={setOnePerPage}
          hasNotes={hasNotes}
          hasAnswers={hasAnswers}
        />

        <Button onClick={() => window.print()}>
          <Printer className="mr-2 h-4 w-4" />
          Spausdinti
        </Button>
      </div>

      <div className="print-sheet mx-auto max-w-3xl">
        {situations.map((situation, index) => (
          <PrintedSheet
            key={situation.key}
            situation={situation}
            showAnswers={showAnswers}
            showNotes={showNotes}
            onePerPage={onePerPage}
            // The name is asked for once, at the top of the set.
            showName={index === 0}
            startOnNewPage={index > 0}
          />
        ))}
      </div>
    </>
  );
}
